// src/pages/finance/TransactionReportPage.tsx
import axios from "axios";
import { saveAs } from "file-saver";
import {
  AlertCircle,
  Banknote,
  CalendarDays,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  Receipt,
  RefreshCw,
  Router,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { API_CONFIG, fetchApp } from "../../services/apiService";

type CustomerSummary = {
  customer_name: string;
  phone_number: string;
  email: string | null;
  created_at: string;
};

type InvoiceSummary = {
  invoice_number: string;
  router_name: string;
  secret_username: string;
  total_amount_cents: number;
  paid_amount_cents: number;
  status: string;
  payment_date: string | null;
  invoice_created_at: string;
};

type ExpenseSummary = {
  category: string | null;
  amount: number;
  currency: string;
  description: string | null;
  expense_date: string;
  created_at: string;
};

type ActivityReport = {
  date_range: string;
  filter: string;
  new_customers_by_router: Record<string, CustomerSummary[]>;
  invoices: InvoiceSummary[];
  expenses: ExpenseSummary[];
  gross_income: number;
  total_expenses: number;
  net_income: number;
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatCurrency = (value: number) =>
  `KES ${new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}`;

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const s = String(status || "").toUpperCase();

  if (s === "PAID" || s === "COMPLETED") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        {s.toLowerCase()}
      </span>
    );
  }

  if (s === "PENDING") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[11px] font-black text-amber-800">
        <Clock className="h-3 w-3" />
        Pending
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
      {status || "Unknown"}
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose" | "violet";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
    violet: {
      card: "border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 shadow-violet-200/50",
      icon: "bg-violet-100 text-violet-700",
      text: "text-violet-700",
      bubble: "bg-violet-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="px-4 py-12 text-center">
    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200 text-slate-500">
      {icon}
    </div>

    <p className="text-[14px] font-black text-slate-800">{title}</p>

    <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
      {description}
    </p>
  </div>
);

const TableSkeleton: React.FC<{ columns: number }> = ({ columns }) => (
  <tr className="animate-pulse">
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0 ? "w-32" : index === columns - 1 ? "w-28" : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="space-y-2">
      <div className="h-4 w-36 rounded bg-slate-200" />
      <div className="h-3 w-24 rounded bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const Panel: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, description, icon, children, right }) => (
  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
    <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          {icon}
        </div>

        <div>
          <h2 className="text-[14px] font-black text-slate-900">{title}</h2>
          <p className="text-[11.5px] font-medium text-slate-500">
            {description}
          </p>
        </div>
      </div>

      {right}
    </div>

    {children}
  </section>
);

const TransactionReportPage: React.FC = () => {
  const { showToast } = useToast();

  const [report, setReport] = useState<ActivityReport | null>(null);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<string>("monthly");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    invoiceNumber: string;
  } | null>(null);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const showError = (msg: string) => {
    setError(msg);
    setSuccess(null);
  };

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setError(null);
  };

  const buildQueryString = (params: { [key: string]: string }) => {
    return Object.keys(params)
      .filter((key) => params[key] !== "")
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`,
      )
      .join("&");
  };

  const fetchReport = async (
    filterValue: string,
    start: string,
    end: string,
  ) => {
    try {
      setLoading(true);
      resetMessages();

      const qs = buildQueryString({
        filter: filterValue,
        start_date: start,
        end_date: end,
      });

      const data = await fetchApp<ActivityReport>(
        `/activity-report${qs ? `?${qs}` : ""}`,
      );

      setReport(data);
    } catch (err: any) {
      const msg = err?.message || "Failed to load report";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_CONFIG.baseUrl}/transactions/download`,
        {
          params: {
            filter,
            start_date: startDate,
            end_date: endDate,
          },
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      saveAs(response.data, "transaction_report.pdf");

      showSuccess("Report downloaded successfully.");
      showToast("Report downloaded successfully", "success");
    } catch (err: any) {
      const msg = err?.message || "Error downloading the report";
      showError(msg);
      showToast(msg, "error");
    }
  };

  useEffect(() => {
    fetchReport(filter, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, startDate, endDate]);

  const stats = useMemo(() => {
    if (!report) {
      return {
        totalInvoices: 0,
        totalCustomers: 0,
        totalExpensesCount: 0,
        grossIncome: 0,
        totalExpenses: 0,
        netIncome: 0,
      };
    }

    const totalInvoices = report.invoices?.length ?? 0;
    const totalExpensesCount = report.expenses?.length ?? 0;
    const totalCustomers = Object.values(
      report.new_customers_by_router ?? {},
    ).reduce((sum, arr) => sum + arr.length, 0);

    return {
      totalInvoices,
      totalCustomers,
      totalExpensesCount,
      grossIncome: Number(report.gross_income || 0),
      totalExpenses: Number(report.total_expenses || 0),
      netIncome: Number(report.net_income || 0),
    };
  }, [report]);

  const customerRouterEntries = Object.entries(
    report?.new_customers_by_router ?? {},
  );

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Periodic Business Report
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Transaction Report
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Summary of new customers, invoices, payments, expenses, gross
                  income, and net income for the selected period.
                </p>

                {report?.date_range && (
                  <p className="mt-2 inline-flex rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-[11.5px] font-bold text-slate-500">
                    Period: {report.date_range}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => fetchReport(filter, startDate, endDate)}
                  disabled={loading}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {loading ? "Refreshing…" : "Refresh"}
                </button>

                <button
                  type="button"
                  onClick={downloadReport}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {loading && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading transaction report…
          </div>
        )}

        {(error || success) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-3xl border px-4 py-3.5 text-[13px] font-bold shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700 shadow-rose-200/40"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-200/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {error ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <p>{error || success}</p>
            </div>

            <button
              type="button"
              onClick={resetMessages}
              className={`rounded-xl p-1.5 transition-colors ${
                error
                  ? "text-rose-600 hover:bg-rose-100"
                  : "text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Filters */}
        <section className="rounded-3xl border border-slate-200 bg-slate-50/95 p-4 shadow-lg shadow-slate-300/25 sm:p-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_2fr] lg:items-end">
            <div>
              <label
                htmlFor="filter"
                className="text-[11px] font-black uppercase tracking-widest text-slate-400"
              >
                Select Date Range
              </label>

              <div className="relative mt-2">
                <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  id="filter"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="yearly">Yearly</option>
                  <option value="custom">Custom Range</option>
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {filter === "custom" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="startDate"
                    className="text-[11px] font-black uppercase tracking-widest text-slate-400"
                  >
                    Start Date
                  </label>

                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${inputCls} mt-2`}
                  />
                </div>

                <div>
                  <label
                    htmlFor="endDate"
                    className="text-[11px] font-black uppercase tracking-widest text-slate-400"
                  >
                    End Date
                  </label>

                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${inputCls} mt-2`}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6 xl:gap-4">
          <StatCard
            label="Invoices"
            value={stats.totalInvoices}
            sub="Invoice records"
            icon={<Receipt className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="New Customers"
            value={stats.totalCustomers}
            sub="Across routers"
            icon={<UserPlus className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Expenses"
            value={stats.totalExpensesCount}
            sub="Expense records"
            icon={<FileText className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Gross Income"
            value={formatCurrency(stats.grossIncome)}
            sub="Total income"
            icon={<TrendingUp className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Total Expenses"
            value={formatCurrency(stats.totalExpenses)}
            sub="Money out"
            icon={<TrendingDown className="h-5 w-5" />}
            tone="rose"
          />

          <StatCard
            label="Net Income"
            value={formatCurrency(stats.netIncome)}
            sub="Income less expenses"
            icon={<Banknote className="h-5 w-5" />}
            tone={stats.netIncome >= 0 ? "emerald" : "rose"}
          />
        </div>

        {/* New Customers by Router */}
        <Panel
          title="New Customers by Router"
          description="Customers created during this reporting period."
          icon={<Users className="h-5 w-5" />}
          right={
            <span className="rounded-full border border-slate-300 bg-slate-200 px-3 py-1 text-[11px] font-black text-slate-600">
              {stats.totalCustomers} customer
              {stats.totalCustomers === 1 ? "" : "s"}
            </span>
          }
        >
          {loading && !report ? (
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full text-left text-[13px]">
                <tbody>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <TableSkeleton key={index} columns={4} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : customerRouterEntries.length === 0 ? (
            <EmptyState
              icon={<Users className="h-7 w-7" />}
              title="No new customers"
              description="No customers were added during this period."
            />
          ) : (
            <div className="divide-y divide-slate-200">
              {customerRouterEntries.map(([routerName, customers]) => (
                <div key={routerName} className="p-4 sm:p-5">
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <Router className="h-4 w-4 text-slate-400" />
                      <h3 className="text-[13.5px] font-black text-slate-900">
                        {routerName}
                      </h3>
                    </div>

                    <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-[11px] font-black text-slate-600">
                      {customers.length} customer
                      {customers.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {/* Desktop table */}
                  <div className="hidden overflow-x-auto rounded-3xl border border-slate-200 md:block">
                    <table className="min-w-full text-left text-[13px]">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                          <th className="px-5 py-3.5 font-black">Customer</th>
                          <th className="px-5 py-3.5 font-black">Phone</th>
                          <th className="px-5 py-3.5 font-black">Email</th>
                          <th className="px-5 py-3.5 font-black">Created</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                        {customers.map((customer, idx) => (
                          <tr
                            key={`${customer.customer_name}-${idx}`}
                            className="transition-colors hover:bg-emerald-50/60"
                          >
                            <td className="px-5 py-4 font-black text-slate-900">
                              {customer.customer_name}
                            </td>

                            <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-600">
                              {customer.phone_number}
                            </td>

                            <td className="px-5 py-4 text-[12.5px] font-medium text-slate-500">
                              {customer.email || "—"}
                            </td>

                            <td className="px-5 py-4 text-[12.5px] font-medium text-slate-500">
                              {formatDateTime(customer.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="space-y-2 md:hidden">
                    {customers.map((customer, idx) => (
                      <div
                        key={`${customer.customer_name}-${idx}`}
                        className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-3"
                      >
                        <p className="text-[13px] font-black text-slate-900">
                          {customer.customer_name}
                        </p>

                        <p className="mt-1 font-mono text-[12px] font-bold text-slate-600">
                          {customer.phone_number}
                        </p>

                        <p className="mt-1 text-[12px] font-medium text-slate-500">
                          {customer.email || "No email"}
                        </p>

                        <p className="mt-2 text-[11px] font-medium text-slate-400">
                          Created: {formatDateTime(customer.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Invoices */}
        <Panel
          title="Transactions: Invoices & Payments"
          description="Invoice totals, paid amounts, payment status, and payment date."
          icon={<Receipt className="h-5 w-5" />}
          right={
            <span className="rounded-full border border-slate-300 bg-slate-200 px-3 py-1 text-[11px] font-black text-slate-600">
              {stats.totalInvoices} invoice
              {stats.totalInvoices === 1 ? "" : "s"}
            </span>
          }
        >
          {loading && !report ? (
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full text-left text-[13px]">
                <tbody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableSkeleton key={index} columns={7} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : !report || report.invoices.length === 0 ? (
            <EmptyState
              icon={<Receipt className="h-7 w-7" />}
              title="No invoices"
              description="No invoice transactions were found for this period."
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-left text-[13px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                      <th className="px-5 py-3.5 font-black">Invoice #</th>
                      <th className="px-5 py-3.5 font-black">Router</th>
                      <th className="px-5 py-3.5 font-black">User</th>
                      <th className="px-5 py-3.5 text-right font-black">
                        Total
                      </th>
                      <th className="px-5 py-3.5 text-right font-black">
                        Paid
                      </th>
                      <th className="px-5 py-3.5 font-black">Status</th>
                      <th className="px-5 py-3.5 font-black">Payment Date</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                    {report.invoices.map((invoice, idx) => (
                      <tr
                        key={`${invoice.invoice_number}-${idx}`}
                        className="transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-4 font-mono text-[12.5px] font-black text-slate-900">
                          {invoice.invoice_number}
                        </td>

                        <td className="px-5 py-4 text-[12.5px] font-bold text-slate-600">
                          {invoice.router_name}
                        </td>

                        <td className="px-5 py-4 text-[12.5px] font-bold text-slate-600">
                          {invoice.secret_username}
                        </td>

                        <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-slate-800">
                          {formatCurrency(invoice.total_amount_cents / 100)}
                        </td>

                        <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-emerald-700">
                          {formatCurrency(invoice.paid_amount_cents / 100)}
                        </td>

                        <td className="px-5 py-4">
                          <StatusPill status={invoice.status} />
                        </td>

                        <td className="px-5 py-4 text-[12.5px] font-medium text-slate-500">
                          {formatDateTime(invoice.payment_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
                {report.invoices.map((invoice, idx) => (
                  <div
                    key={`${invoice.invoice_number}-${idx}`}
                    className="px-4 py-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-mono text-[13px] font-black text-slate-900">
                          {invoice.invoice_number}
                        </p>

                        <p className="mt-0.5 truncate text-[12px] font-bold text-slate-500">
                          {invoice.secret_username} · {invoice.router_name}
                        </p>
                      </div>

                      <StatusPill status={invoice.status} />
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Total
                        </p>
                        <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                          {formatCurrency(invoice.total_amount_cents / 100)}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                          Paid
                        </p>
                        <p className="mt-1 font-mono text-[12.5px] font-black text-emerald-800">
                          {formatCurrency(invoice.paid_amount_cents / 100)}
                        </p>
                      </div>
                    </div>

                    <p className="mt-3 text-[11.5px] font-medium text-slate-500">
                      Payment date: {formatDateTime(invoice.payment_date)}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>

        {/* Expenses */}
        <Panel
          title="Expenses"
          description="Expense records captured during the reporting period."
          icon={<FileText className="h-5 w-5" />}
          right={
            <span className="rounded-full border border-slate-300 bg-slate-200 px-3 py-1 text-[11px] font-black text-slate-600">
              {stats.totalExpensesCount} expense
              {stats.totalExpensesCount === 1 ? "" : "s"}
            </span>
          }
        >
          {loading && !report ? (
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full text-left text-[13px]">
                <tbody>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <TableSkeleton key={index} columns={4} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : !report || report.expenses.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-7 w-7" />}
              title="No expenses"
              description="No expenses were recorded for this period."
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-left text-[13px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                      <th className="px-5 py-3.5 font-black">Category</th>
                      <th className="px-5 py-3.5 text-right font-black">
                        Amount
                      </th>
                      <th className="px-5 py-3.5 font-black">Description</th>
                      <th className="px-5 py-3.5 font-black">Expense Date</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                    {report.expenses.map((expense, idx) => (
                      <tr
                        key={`${expense.category}-${idx}`}
                        className="transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-4 text-[13px] font-black text-slate-900">
                          {expense.category || "—"}
                        </td>

                        <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-rose-700">
                          {formatCurrency(expense.amount)}
                        </td>

                        <td className="max-w-[360px] px-5 py-4">
                          <p className="truncate text-[12.5px] font-medium text-slate-500">
                            {expense.description || "—"}
                          </p>
                        </td>

                        <td className="px-5 py-4 text-[12.5px] font-medium text-slate-500">
                          {formatDate(expense.expense_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
                {report.expenses.map((expense, idx) => (
                  <div key={`${expense.category}-${idx}`} className="px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[13px] font-black text-slate-900">
                          {expense.category || "Uncategorised"}
                        </p>

                        <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                          {formatDate(expense.expense_date)}
                        </p>
                      </div>

                      <p className="font-mono text-[12.5px] font-black text-rose-700">
                        {formatCurrency(expense.amount)}
                      </p>
                    </div>

                    {expense.description && (
                      <p className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-2 text-[12px] font-medium text-slate-600">
                        {expense.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>

        {/* Confirm delete dialog */}
        <ConfirmDialog
          open={confirmOpen}
          loading={confirmLoading}
          title="Delete Transaction?"
          description={
            pendingDelete
              ? `Are you sure you want to delete transaction "${pendingDelete.invoiceNumber}"?`
              : ""
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          onCancel={() => {
            setConfirmOpen(false);
            setPendingDelete(null);
          }}
          onConfirm={() => {
            setConfirmLoading(false);
          }}
        />
      </div>
    </div>
  );
};

export default TransactionReportPage;
